import React, { useEffect, useState } from 'react';
import { Container } from 'reactstrap';
import { observer } from 'mobx-react';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import {
    Block,
    Heading3,
    TextDefault,
    TextTiny,
    ThemedButton,
    FlexDiv,
    palette,
} from '@awarego-ui/components';
import HeaderSection from '../../components/header/HeaderSection';
import SubHeaderText from '../../components/header/SubHeaderText';
import SubHeaderComponentsList from '../../components/header/SubHeaderComponentsList';
import { useStores } from '../../hooks/use-stores';
import AwarePhishSubNav from './AwarePhishSubNav';

const FieldLabel = styled.div`
    font-size: 11px;
    font-weight: 600;
    color: ${palette.graphite.charcoal};
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin: 12px 0 6px;
`;

const StatusPill = styled.span`
    display: inline-block;
    padding: 4px 10px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
    background: ${palette.graphite.cloud};
    color: ${palette.graphite.charcoal};
    text-transform: capitalize;
`;

const StepCard = styled.div`
    border: 1px solid ${palette.graphite.silver};
    border-radius: 6px;
    padding: 12px 14px;
    margin-bottom: 10px;
`;

const Snippet = styled.pre`
    background: ${palette.graphite.cloud};
    padding: 12px;
    font-size: 12px;
    overflow: auto;
    border-radius: 4px;
    margin: 6px 0 12px;
`;

const Select = styled.select`
    width: 100%;
    padding: 8px;
    font-size: 13px;
`;

function statusLabel(value) {
    return String(value || 'not_started').replace(/_/g, ' ');
}

function AwarePhishDelivery() {
    const { store, awarePhishStore, commonStore } = useStores();
    const { currentCompany } = store;
    const companyId = currentCompany && currentCompany.company_id;
    const { delivery, loadingDelivery, savingAsset } = awarePhishStore;

    const [mailPlatform, setMailPlatform] = useState('m365');
    const [senderDomainId, setSenderDomainId] = useState('');
    const [landingDomainId, setLandingDomainId] = useState('');
    const [deliveryStatus, setDeliveryStatus] = useState('not_started');
    const [reportingStatus, setReportingStatus] = useState('not_started');
    const [reportingMailbox, setReportingMailbox] = useState('');

    useEffect(() => {
        if (companyId) awarePhishStore.loadDelivery(companyId);
    }, [companyId]);

    useEffect(() => {
        if (!delivery) return;
        setMailPlatform(delivery.mailPlatform || 'm365');
        setSenderDomainId(delivery.senderDomainId || '');
        setLandingDomainId(delivery.landingDomainId || '');
        setDeliveryStatus(delivery.deliveryStatus || 'not_started');
        setReportingStatus(delivery.reportingStatus || 'not_started');
        setReportingMailbox(delivery.reportingMailbox || '');
    }, [delivery]);

    const save = async (extra = {}) => {
        try {
            await awarePhishStore.saveDelivery(companyId, {
                mailPlatform,
                senderDomainId: senderDomainId || null,
                landingDomainId: landingDomainId || null,
                deliveryStatus,
                reportingStatus,
                reportingMailbox: reportingMailbox || null,
                markInstructed: true,
                ...extra,
            });
            commonStore.success('Delivery settings saved');
        } catch (e) {
            commonStore.error(
                awarePhishStore.error || 'Failed to save delivery settings'
            );
        }
    };

    const instructions = delivery && delivery.instructions;
    const senders = (delivery && delivery.availableSenders) || [];
    const landings = (delivery && delivery.availableLandings) || [];
    const dkimAvailable = Boolean(delivery && delivery.dkimAvailable);

    return (
        <Container className="manager-content">
            <Helmet>
                <title>AwarePHISH delivery</title>
            </Helmet>
            <HeaderSection
                heading="Delivery & reporting"
                subHeadings={
                    <SubHeaderComponentsList>
                        <SubHeaderText
                            text={
                                'Configure how simulation mail reaches your organisation and how Report phishing will be collected. Domain infrastructure is managed by AwareGO; you allowlist it in your mail system.'
                            }
                        />
                    </SubHeaderComponentsList>
                }
            />
            <AwarePhishSubNav active="delivery" />

            {loadingDelivery && !delivery ? (
                <TextDefault lighter>Loading…</TextDefault>
            ) : (
                <>
                    <Block padding="20" column style={{ marginBottom: 14 }}>
                        <Heading3>Status</Heading3>
                        <FlexDiv gap={16} style={{ marginTop: 8 }}>
                            <div>
                                <TextTiny lighter>Delivery</TextTiny>
                                <div>
                                    <StatusPill>
                                        {statusLabel(
                                            (delivery &&
                                                delivery.deliveryStatus) ||
                                                'not_started'
                                        )}
                                    </StatusPill>
                                </div>
                            </div>
                            <div>
                                <TextTiny lighter>Reporting</TextTiny>
                                <div>
                                    <StatusPill>
                                        {statusLabel(
                                            (delivery &&
                                                delivery.reportingStatus) ||
                                                'not_started'
                                        )}
                                    </StatusPill>
                                </div>
                            </div>
                        </FlexDiv>

                        <FieldLabel>Mail platform</FieldLabel>
                        <Select
                            value={mailPlatform}
                            onChange={(e) => setMailPlatform(e.target.value)}
                        >
                            <option value="m365">Microsoft 365</option>
                            <option value="google">Google Workspace</option>
                            <option value="other">Other / on-prem</option>
                        </Select>

                        <FieldLabel>Sender domain</FieldLabel>
                        <Select
                            value={senderDomainId}
                            onChange={(e) => setSenderDomainId(e.target.value)}
                        >
                            <option value="">Auto (recommended)</option>
                            {senders.map((d) => (
                                <option key={d.id} value={d.id}>
                                    {d.hostname}
                                </option>
                            ))}
                        </Select>
                        {!senders.length ? (
                            <TextTiny lighter style={{ marginTop: 6 }}>
                                Sender domains are not ready yet. AwareGO will
                                finish infrastructure setup — contact support if
                                this persists.
                            </TextTiny>
                        ) : null}

                        <FieldLabel>Landing domain</FieldLabel>
                        <Select
                            value={landingDomainId}
                            onChange={(e) => setLandingDomainId(e.target.value)}
                        >
                            <option value="">Auto (recommended)</option>
                            {landings.map((d) => (
                                <option key={d.id} value={d.id}>
                                    {d.hostname}
                                </option>
                            ))}
                        </Select>

                        <FieldLabel>Delivery status</FieldLabel>
                        <Select
                            value={deliveryStatus}
                            onChange={(e) => setDeliveryStatus(e.target.value)}
                        >
                            <option value="not_started">Not started</option>
                            <option value="instructed">Instructed</option>
                            <option value="configured">Configured</option>
                            <option value="verified">Verified</option>
                            <option value="failed">Failed</option>
                        </Select>

                        <FieldLabel>Reporting status</FieldLabel>
                        <Select
                            value={reportingStatus}
                            onChange={(e) => setReportingStatus(e.target.value)}
                        >
                            <option value="not_started">Not started</option>
                            <option value="instructed">Instructed</option>
                            <option value="configured">Configured</option>
                            <option value="verified">Verified</option>
                            <option value="failed">Failed</option>
                        </Select>

                        <FieldLabel>
                            Reporting mailbox (optional, Microsoft 365)
                        </FieldLabel>
                        <input
                            style={{
                                width: '100%',
                                padding: 8,
                                fontSize: 13,
                                boxSizing: 'border-box',
                            }}
                            value={reportingMailbox}
                            onChange={(e) =>
                                setReportingMailbox(e.target.value)
                            }
                            placeholder="phishing-reports@yourcompany.com"
                        />

                        <FlexDiv gap={12} style={{ marginTop: 16 }} $noWrap>
                            <ThemedButton
                                text={savingAsset ? 'Saving…' : 'Save'}
                                mode="primary"
                                onClick={() => save()}
                                disabled={savingAsset}
                                $noWrap
                            />
                            <ThemedButton
                                text="Mark delivery verified"
                                mode="secondary"
                                onClick={() =>
                                    save({ deliveryStatus: 'verified' })
                                }
                                disabled={savingAsset}
                                $noWrap
                            />
                        </FlexDiv>
                    </Block>

                    <Block padding="20" column style={{ marginBottom: 14 }}>
                        <Heading3>DKIM identity</Heading3>
                        <TextTiny lighter style={{ marginBottom: 8 }}>
                            Your organisation’s DKIM domain for Microsoft 365
                            Advanced Delivery. Add it (with the sending IPs in
                            the steps below) under Phishing simulations.
                        </TextTiny>
                        <TextDefault>
                            {(delivery && delivery.dkimHostname) ||
                                'Not issued yet'}
                        </TextDefault>
                        {delivery && delivery.dkimIssuedAt ? (
                            <TextTiny lighter>
                                Issued:{' '}
                                {new Date(
                                    delivery.dkimIssuedAt
                                ).toLocaleString()}
                            </TextTiny>
                        ) : null}
                        {!dkimAvailable && !(delivery && delivery.dkimHostname) ? (
                            <TextTiny lighter style={{ marginTop: 8 }}>
                                DKIM identity is not available yet. AwareGO will
                                enable it for your organisation.
                            </TextTiny>
                        ) : (
                            <FlexDiv gap={12} style={{ marginTop: 12 }}>
                                <ThemedButton
                                    text={
                                        savingAsset
                                            ? 'Working…'
                                            : delivery && delivery.dkimHostname
                                              ? 'Re-issue DKIM hostname'
                                              : 'Issue DKIM hostname'
                                    }
                                    mode="secondary"
                                    onClick={async () => {
                                        try {
                                            const r =
                                                await awarePhishStore.issueDkim(
                                                    companyId
                                                );
                                            commonStore.success(
                                                `DKIM ready: ${
                                                    (r &&
                                                        r.issuedDkimHostname) ||
                                                    (r && r.dkimHostname) ||
                                                    'issued'
                                                }`
                                            );
                                            await awarePhishStore.loadDelivery(
                                                companyId
                                            );
                                        } catch (e) {
                                            commonStore.error(
                                                awarePhishStore.error ||
                                                    'Could not issue DKIM identity'
                                            );
                                        }
                                    }}
                                    disabled={savingAsset || !dkimAvailable}
                                    $noWrap
                                />
                            </FlexDiv>
                        )}
                    </Block>

                    <Block padding="20" column style={{ marginBottom: 14 }}>
                        <Heading3>Report phishing sync (Microsoft 365)</Heading3>
                        <TextTiny lighter style={{ marginBottom: 8 }}>
                            Reads your reporting mailbox and marks matching
                            simulation recipients as Reported. Requires the
                            mailbox above and Microsoft 365 connection set up by
                            AwareGO.
                        </TextTiny>
                        {delivery && delivery.lastReportSyncAt ? (
                            <TextTiny lighter>
                                Last sync:{' '}
                                {new Date(
                                    delivery.lastReportSyncAt
                                ).toLocaleString()}
                            </TextTiny>
                        ) : (
                            <TextTiny lighter>Never synced</TextTiny>
                        )}
                        <FlexDiv gap={12} style={{ marginTop: 12 }}>
                            <ThemedButton
                                text={
                                    savingAsset
                                        ? 'Syncing…'
                                        : 'Sync reports now'
                                }
                                mode="primary"
                                onClick={async () => {
                                    try {
                                        const r =
                                            await awarePhishStore.syncReports(
                                                companyId
                                            );
                                        commonStore.success(
                                            `Scanned ${
                                                (r && r.scanned) || 0
                                            }, reported ${
                                                (r && r.reported) || 0
                                            }, skipped ${
                                                (r && r.skipped) || 0
                                            }`
                                        );
                                    } catch (e) {
                                        commonStore.error(
                                            awarePhishStore.error ||
                                                'Report sync failed'
                                        );
                                    }
                                }}
                                disabled={
                                    savingAsset ||
                                    !(delivery && delivery.reportingMailbox)
                                }
                                $noWrap
                            />
                        </FlexDiv>
                    </Block>

                    {instructions ? (
                        <Block padding="20" column>
                            <Heading3>{instructions.title}</Heading3>
                            <TextTiny lighter style={{ marginBottom: 12 }}>
                                Domains:{' '}
                                {instructions.senderDomain || '—'} · IPs:{' '}
                                {(instructions.sendingIps || []).join(', ') ||
                                    '—'}{' '}
                                · Landing: {instructions.landingUrl || '—'}
                            </TextTiny>
                            {(instructions.steps || []).map((s) => (
                                <StepCard key={s.id}>
                                    <TextDefault bold>{s.title}</TextDefault>
                                    <TextTiny
                                        lighter
                                        style={{ marginTop: 4, display: 'block' }}
                                    >
                                        {s.detail}
                                    </TextTiny>
                                </StepCard>
                            ))}
                            {instructions.snippets &&
                            instructions.snippets.powershell ? (
                                <>
                                    <FieldLabel>PowerShell helper</FieldLabel>
                                    <Snippet>
                                        {instructions.snippets.powershell}
                                    </Snippet>
                                </>
                            ) : null}
                        </Block>
                    ) : null}
                </>
            )}
        </Container>
    );
}

export default observer(AwarePhishDelivery);
